import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllClaims, updateClaimStatus, selectClaims, selectClaimsLoading, selectClaimsError } from '../../features/claims/claimSlice';
import { useToast } from '../../contexts/ToastContext';
import LoadingState from '../ui/LoadingState';
import ErrorMessage from '../ClaimsList/ErrorMessage';
import DashboardLayout from '../layout/DashboardLayout';
import DashboardSection, { CardSection } from '../layout/DashboardSection';
import { useDashboardStatistics } from '../../hooks/useDashboardStats';

const PaymentManagement = () => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const claims = useSelector(selectClaims);
  const loading = useSelector(selectClaimsLoading);
  const error = useSelector(selectClaimsError);
  const [filteredClaims, setFilteredClaims] = useState([]);
  const [processingPayments, setProcessingPayments] = useState(new Set());
  const { statistics, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = useDashboardStatistics();

  // Load claims on component mount
  useEffect(() => {
    dispatch(getAllClaims());
  }, [dispatch]);

  // Filter claims for payment processing (APPROVED or PAYMENT_PENDING)
  useEffect(() => {
    if (!claims || !Array.isArray(claims)) {
      setFilteredClaims([]);
      return;
    }
    
    const paymentClaims = claims.filter(claim => 
      claim.status === 'APPROVED' || claim.status === 'PAYMENT_PENDING'
    );
    setFilteredClaims(paymentClaims);
  }, [claims]);

  // Calculate summary statistics (prefer backend statistics when available)
  const backendCounts = statistics?.counts || {};
  const summaryStats = {
    totalClaims: Number(backendCounts.total) || Number(backendCounts.all) || filteredClaims.length,
    approvedClaims: Number(backendCounts.approved) || filteredClaims.filter(c => c.status === 'APPROVED').length,
    pendingPayment: filteredClaims.filter(c => c.status === 'PAYMENT_PENDING').length,
    totalAmount: filteredClaims.reduce((sum, claim) => sum + (parseFloat(claim.benefitAmount) || 0), 0)
  };

  const handleStatusUpdate = async (claimId, newStatus) => {
    if (processingPayments.has(claimId)) return;

    setProcessingPayments(prev => new Set(prev).add(claimId));
    
    try {
      await dispatch(updateClaimStatus({ 
        claimId, 
        status: newStatus,
        comment: `Payment status updated to ${newStatus} by Finance Officer`
      })).unwrap();
      
      showSuccess(`Claim status updated to ${newStatus}`);
      
      // Refresh claims list and backend statistics for consistent counts
      dispatch(getAllClaims());
      refetchStats();
    } catch (error) {
      console.error('Failed to update claim status:', error);
      showError('Failed to update claim status');
    } finally {
      setProcessingPayments(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(amount || 0);
  };

  const formatDate = (dateLike) => {
    if (!dateLike) return '—';
    const date = typeof dateLike === 'string' || typeof dateLike === 'number' ? new Date(dateLike) : dateLike;
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GH');
  };

  const getSubmittedDate = (claim) => {
    // Prefer backend canonical fields, then fall back to legacy names
    return claim?.submittedAt || claim?.createdAt || claim?.submissionDate || null;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'APPROVED': 'bg-green-100 text-green-800',
      'PAYMENT_PENDING': 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  if ((loading || statsLoading) && (!claims || claims.length === 0)) {
    return <LoadingState message="Loading payment claims..." />;
  }

  if ((error || statsError) && (!claims || claims.length === 0)) {
    return <ErrorMessage isError={true} message="Failed to load claims for payment processing" />;
  }

  return (
    <DashboardLayout>
      <DashboardSection
        title="Payment Management"
        subtitle="Process payments for approved claims and manage payment status"
      >
        {/* Summary Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-2">
          <CardSection>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-600 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">#{summaryStats.totalClaims}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Claims</dt>
                  <dd className="text-lg font-medium text-gray-900">{summaryStats.totalClaims}</dd>
                </dl>
              </div>
            </div>
          </CardSection>

          <CardSection>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">#{summaryStats.approvedClaims}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Ready for Payment</dt>
                  <dd className="text-lg font-medium text-gray-900">{summaryStats.approvedClaims}</dd>
                </dl>
              </div>
            </div>
          </CardSection>

          <CardSection>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-400 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">#{summaryStats.pendingPayment}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Payment Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">{summaryStats.pendingPayment}</dd>
                </dl>
              </div>
            </div>
          </CardSection>

          <CardSection>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-700 rounded-md flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">₵</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Amount</dt>
                  <dd className="text-lg font-medium text-gray-900">{formatCurrency(summaryStats.totalAmount)}</dd>
                </dl>
              </div>
            </div>
          </CardSection>
        </div>

        {/* Claims Table */}
        <CardSection>
          <div className="px-1 py-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Claims for Payment Processing</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Claims that are approved and ready for payment processing
            </p>
          </div>
          {filteredClaims.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <div className="text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No claims ready for payment</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Claims will appear here when they are approved and ready for payment processing.
                </p>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredClaims.map((claim) => (
                <li key={claim.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {claim.claimType?.charAt(0) || 'C'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">
                            {claim.claimNumber}
                          </p>
                          <div className="ml-2">
                            {getStatusBadge(claim.status)}
                          </div>
                        </div>
                        <div className="mt-1">
                          <p className="text-sm text-gray-600">
                            {claim.claimantName} • {claim.claimType}
                          </p>
                          <p className="text-sm text-gray-500">
                            Submitted: {formatDate(getSubmittedDate(claim))}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(claim.benefitAmount)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {claim.scheme?.name || 'N/A'}
                        </p>
                      </div>
                      
                      <div className="flex space-x-2">
                        {claim.status === 'APPROVED' && (
                          <button
                            onClick={() => handleStatusUpdate(claim.id, 'PAYMENT_PENDING')}
                            disabled={processingPayments.has(claim.id)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingPayments.has(claim.id) ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Processing...
                              </>
                            ) : (
                              'Process Payment'
                            )}
                          </button>
                        )}
                        
                        {claim.status === 'PAYMENT_PENDING' && (
                          <button
                            onClick={() => handleStatusUpdate(claim.id, 'PAID')}
                            disabled={processingPayments.has(claim.id)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingPayments.has(claim.id) ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Marking as Paid...
                              </>
                            ) : (
                              'Mark as Paid'
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardSection>
      </DashboardSection>
    </DashboardLayout>
  );
};

export default PaymentManagement;